import type {
  AcceptanceDashboardDateField,
  ActionDashboardDateField,
  AssessmentDashboardDateField,
  AttestationDashboardDateField,
  CauseDashboardDateField,
  ChangeRequestDashboardDateField,
  ConsequenceDashboardDateField,
  ControlDashboardDateField,
  DocumentDashboardDateField,
  IndicatorDashboardDateField,
  InternalAuditEntityDashboardDateField,
  IssueDashboardDateField,
  ObligationDashboardDateField,
  OversightDashboardDateField,
  RiskDashboardDateField,
  TestResultDashboardDateField,
  ThirdPartyDashboardDateField,
} from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type {
  Acceptance_Bool_Exp,
  Action_Bool_Exp,
  Assessment_Bool_Exp,
  Attestation_Record_Bool_Exp,
  Cause_Bool_Exp,
  Change_Request_Bool_Exp,
  Consequence_Bool_Exp,
  Control_Bool_Exp,
  GetDocumentsQueryVariables,
  Indicator_Bool_Exp,
  Internal_Audit_Entity_Bool_Exp,
  Issue_Bool_Exp,
  Obligation_Bool_Exp,
  Oversight_Assessment_Bool_Exp,
  Risk_Bool_Exp,
  Test_Result_Bool_Exp,
  Third_Party_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { Assessment_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';

import type { DateBounds } from '@/hooks/queries/dashboardFilterArgs';

/**
 * Every register entity's dashboard date-field map in one place (COI-691).
 *
 * Each map is an exhaustive `Record` over its entity's date-field union from
 * `@risksmart-app/domain/src/types/consts/dashboard-date-fields`, so a date
 * field added server-side fails `tsc` here until it is mapped. That
 * exhaustiveness is the compile-time net this ticket relies on in place of
 * unit tests.
 *
 * Entries return a VARIABLES fragment rather than a bare `Bool_Exp` because
 * the document register's `ReviewDate` / `NextReviewDate` ride a separate
 * `filesWhere` variable; `buildDashboardWhere` reads `.where` and the document
 * hook reads the rest.
 */

/**
 * `ActualCloseDate` / `TargetCloseDate` are issue REGISTER columns but live on
 * `issue_assessment`, reached through the `assessment` one-relation.
 */
export const ISSUE_DATE_PREDICATES: Record<
  IssueDashboardDateField,
  (bounds: DateBounds) => { where: Issue_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  DateIdentified: (b) => ({ where: { DateIdentified: b } }),
  DateOccurred: (b) => ({ where: { DateOccurred: b } }),
  RaisedAtTimestamp: (b) => ({ where: { RaisedAtTimestamp: b } }),
  ActualCloseDate: (b) => ({ where: { assessment: { ActualCloseDate: b } } }),
  TargetCloseDate: (b) => ({ where: { assessment: { TargetCloseDate: b } } }),
};

/**
 * `IssueRaisedDate` / `IssueClosedDate` are cause REGISTER column names, not
 * cause columns: they traverse `cause.issue` to `issue.CreatedAtTimestamp` and
 * `issue.assessment.ActualCloseDate`. Cause carries its own
 * `CreatedAtTimestamp`, which is why the traversed columns could not be named
 * after themselves (see `dashboard-date-fields.ts`).
 */
export const CAUSE_DATE_PREDICATES: Record<
  CauseDashboardDateField,
  (bounds: DateBounds) => { where: Cause_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  IssueRaisedDate: (b) => ({ where: { issue: { CreatedAtTimestamp: b } } }),
  IssueClosedDate: (b) => ({
    where: { issue: { assessment: { ActualCloseDate: b } } },
  }),
};

/**
 * `IssueRaisedDate` / `IssueClosedDate` traverse the `consequence.issue`
 * one-relation — they are consequence REGISTER column names, not consequence
 * columns. Tags and departments live on the issue too, matching the descriptor.
 */
export const CONSEQUENCE_DATE_PREDICATES: Record<
  ConsequenceDashboardDateField,
  (bounds: DateBounds) => { where: Consequence_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  IssueRaisedDate: (b) => ({ where: { issue: { CreatedAtTimestamp: b } } }),
  IssueClosedDate: (b) => ({
    where: { issue: { assessment: { ActualCloseDate: b } } },
  }),
};

/**
 * `NextTestDate` / `NextTestOverdueDate` are NOT risk columns — they are
 * `schedule_state.DueDate` / `.OverdueDate` via the `scheduleState`
 * one-relation. A risk with no schedule_state row is excluded by either.
 */
export const RISK_DATE_PREDICATES: Record<
  RiskDashboardDateField,
  (bounds: DateBounds) => { where: Risk_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  NextTestDate: (b) => ({ where: { scheduleState: { DueDate: b } } }),
  NextTestOverdueDate: (b) => ({
    where: { scheduleState: { OverdueDate: b } },
  }),
};

/**
 * `NextTestDate` / `NextTestOverdueDate` are NOT control columns — they are
 * `schedule_state.DueDate` / `.OverdueDate` via the `scheduleState`
 * one-relation. A control with no schedule_state row is excluded by either.
 */
export const CONTROL_DATE_PREDICATES: Record<
  ControlDashboardDateField,
  (bounds: DateBounds) => { where: Control_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  NextTestDate: (b) => ({ where: { scheduleState: { DueDate: b } } }),
  NextTestOverdueDate: (b) => ({
    where: { scheduleState: { OverdueDate: b } },
  }),
};

/**
 * Both date fields are own columns on `test_result`. Tags and departments are
 * NOT — they live on the parent control, reached via the `parent` manual object
 * relationship, which is exactly what the widget's nested
 * `{ control: [{ testResults }] }` document existed to express (COI-694).
 */
export const TEST_RESULT_DATE_PREDICATES: Record<
  TestResultDashboardDateField,
  (bounds: DateBounds) => { where: Test_Result_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  TestDate: (b) => ({ where: { TestDate: b } }),
};

export const ACTION_DATE_PREDICATES: Record<
  ActionDashboardDateField,
  (bounds: DateBounds) => { where: Action_Bool_Exp }
> = {
  DateRaised: (b) => ({ where: { DateRaised: b } }),
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  DateDue: (b) => ({ where: { DateDue: b } }),
};

/**
 * `LatestResultDate` filters the `results` MANY-relation — "has ANY result in
 * range" — while the register column displays the LATEST result's date. An
 * indicator can therefore be matched by a January filter and grouped under
 * March. Inherited from the descriptor; parity, not correctness, is the
 * contract here (see `dashboard-date-fields.ts`).
 */
export const INDICATOR_DATE_PREDICATES: Record<
  IndicatorDashboardDateField,
  (bounds: DateBounds) => { where: Indicator_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  LatestResultDate: (b) => ({ where: { results: { ResultDate: b } } }),
};

export const ASSESSMENT_DATE_PREDICATES: Record<
  AssessmentDashboardDateField,
  (bounds: DateBounds) => { where: Assessment_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  StartDate: (b) => ({ where: { StartDate: b } }),
  ActualCompletionDate: (b) => ({ where: { ActualCompletionDate: b } }),
};

export const OVERSIGHT_DATE_PREDICATES: Record<
  OversightDashboardDateField,
  (bounds: DateBounds) => { where: Oversight_Assessment_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  StartDate: (b) => ({ where: { StartDate: b } }),
  TargetCompletionDate: (b) => ({ where: { TargetCompletionDate: b } }),
  ActualCompletionDate: (b) => ({ where: { ActualCompletionDate: b } }),
};

export const OBLIGATION_DATE_PREDICATES: Record<
  ObligationDashboardDateField,
  (bounds: DateBounds) => { where: Obligation_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
};

export const THIRD_PARTY_DATE_PREDICATES: Record<
  ThirdPartyDashboardDateField,
  (bounds: DateBounds) => { where: Third_Party_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
};

/**
 * The only date map that targets more than one variable: `ReviewDate` and
 * `NextReviewDate` live on `document_file` and ride `filesWhere`, where they
 * narrow the nested FILE selection and never the document row set. That is a
 * known Apollo-leg bug which the tRPC leg deliberately fixes by resolving both
 * against the newest file (COI-696). Reproduced verbatim so B3 stays additive —
 * the legs already diverge here and closing it is COI-696's job.
 */
export const DOCUMENT_DATE_PREDICATES: Record<
  DocumentDashboardDateField,
  (bounds: DateBounds) => Partial<GetDocumentsQueryVariables>
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  ReviewDate: (b) => ({ filesWhere: { ReviewDate: b } }),
  NextReviewDate: (b) => ({ filesWhere: { NextReviewDate: b } }),
};

/**
 * `LatestReportDate` is NOT a column on `internal_audit_entity` — there is no
 * such field on `Internal_Audit_Entity_Bool_Exp`. `dashboard-date-fields.ts`
 * defines it as the greatest `ActualCompletionDate` across the entity's
 * `complete` reports, which the tRPC repository computes (COI-696). Hasura
 * cannot express "greatest", so the Apollo leg emits the nearest predicate it
 * has — "has ANY completed report in range". The two legs differ here for an
 * entity with several completed reports; closing that belongs with COI-696.
 */
export const INTERNAL_AUDIT_DATE_PREDICATES: Record<
  InternalAuditEntityDashboardDateField,
  (bounds: DateBounds) => { where: Internal_Audit_Entity_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  LatestReportDate: (b) => ({
    where: {
      internalAuditReports: {
        Status: { _eq: Assessment_Status_Enum.Complete },
        ActualCompletionDate: b,
      },
    },
  }),
};

/**
 * `DateAccepted` is a CONTAINMENT predicate across two columns: the accepted
 * period must sit inside the requested range (`DateAcceptedFrom >= from` AND
 * `DateAcceptedTo <= to`). It is deliberately not interval overlap — that is
 * what the descriptor's paired `_gte` / `_lte` mean and parity with it is the
 * contract. Tags, departments and ownership all traverse `parents.risk`.
 */
export const ACCEPTANCE_DATE_PREDICATES: Record<
  AcceptanceDashboardDateField,
  (bounds: DateBounds) => { where: Acceptance_Bool_Exp }
> = {
  DateAccepted: ({ _gte, _lte }) => ({
    where: {
      DateAcceptedFrom: { _gte },
      DateAcceptedTo: { _lte },
    },
  }),
};

/**
 * All three fields are own columns on `attestation_record`. Attestation
 * declares no tag or department filter and its ownership story is the
 * pre-existing per-user `userId` arg, not this shared eight-flag fragment —
 * so this is the entire dashboard-filter surface for attestation.
 */
export const ATTESTATION_DATE_PREDICATES: Record<
  AttestationDashboardDateField,
  (bounds: DateBounds) => { where: Attestation_Record_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
  AttestedAt: (b) => ({ where: { AttestedAt: b } }),
  ExpiresAt: (b) => ({ where: { ExpiresAt: b } }),
};

/**
 * Change request declares no tag or department filter and its ownership
 * narrowing is client-side (the descriptor's `isActiveApprover` pass, not this
 * shared eight-flag fragment), so the dashboard half is dates only — one own
 * column on `change_request`. That pass picks which readable rows to surface;
 * read authorisation is perm_v2 + RLS server-side, never this.
 */
export const CHANGE_REQUEST_DATE_PREDICATES: Record<
  ChangeRequestDashboardDateField,
  (bounds: DateBounds) => { where: Change_Request_Bool_Exp }
> = {
  CreatedAtTimestamp: (b) => ({ where: { CreatedAtTimestamp: b } }),
};
